import { memo, useState, useEffect } from 'react';

interface CacheIndicatorProps {
  show?: boolean;
}

/**
 * Indicateur visuel du statut du cache et du mode offline
 */
export const CacheIndicator = memo(({ show = true }: CacheIndicatorProps) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cacheStats, setCacheStats] = useState<{
    totalSize: number;
    totalItems: number;
  } | null>(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const fetchCacheStats = async () => {
      if (!('serviceWorker' in navigator)) {
        return;
      }

      try {
        const registration = await navigator.serviceWorker.ready;
        
        if (registration.active) {
          const messageChannel = new MessageChannel();
          
          messageChannel.port1.onmessage = (event) => {
            const stats = event.data;
            let totalSize = 0;
            let totalItems = 0;

            Object.values(stats).forEach((cache: any) => {
              totalSize += cache.size;
              totalItems += cache.count;
            });

            setCacheStats({ totalSize, totalItems });
          };

          registration.active.postMessage(
            { type: 'GET_CACHE_STATS' },
            [messageChannel.port2]
          );
        }
      } catch (err) {
        // Silently fail in production
      }
    };

    fetchCacheStats();
    const interval = setInterval(fetchCacheStats, 30000); // Mise à jour toutes les 30s

    return () => clearInterval(interval);
  }, []);

  if (!show) {
    return null;
  }

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {/* Indicateur de connexion */}
      <div
        className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg backdrop-blur-sm transition-all ${
          isOnline
            ? 'bg-emerald-500/90 text-white'
            : 'bg-orange-500/90 text-white'
        }`}
      >
        <div
          className={`w-2 h-2 rounded-full ${
            isOnline ? 'bg-white animate-pulse-slow' : 'bg-white'
          }`}
        />
        <span className="text-sm font-medium whitespace-nowrap">
          {isOnline ? 'En ligne' : 'Hors ligne'}
        </span>
      </div>

      {/* Stats du cache */}
      {cacheStats && cacheStats.totalItems > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/90 text-white shadow-lg backdrop-blur-sm">
          <i className="ri-database-2-line text-sm" />
          <span className="text-sm font-medium whitespace-nowrap">
            {cacheStats.totalItems} éléments • {formatBytes(cacheStats.totalSize)}
          </span>
        </div>
      )}
    </div>
  );
});

CacheIndicator.displayName = 'CacheIndicator';